import type { SubscriptionPlan } from '../../types/subscription'
import { useT } from '../../i18n/useT'
import { FREE_MONTHLY_CREDITS } from '../../lib/freeAllowance'
import { FreePlanCard } from './FreePlanCard'
import { PlanCard } from './PlanCard'

interface PricingSectionProps {
  onSubscribe: (planName: string) => void
  onCancelSubscription?: () => void
  currentPlan?: string | null
  isSubscribed?: boolean
  /** What this account has left — the free rung shows it instead of an offer
   * once someone is registered */
  credits?: number
  /** The profile is still loading; the free rung waits rather than guess */
  creditsLoading?: boolean
  /** When this account's transcriptions come back — the 1st of next month
   * for a free account, the billing date for a subscriber (null when there
   * is no honest date). The free rung reads it as "back to 3 on …", the
   * current paid card as "Renews on …". */
  renewsOn?: Date | null
}

export function PricingSection({
  onSubscribe,
  onCancelSubscription,
  currentPlan,
  isSubscribed,
  credits = 0,
  creditsLoading = false,
  renewsOn = null,
}: PricingSectionProps) {
  const t = useT()

  // Every tier is the same product in a different amount — nothing here is
  // missing from the free one. Said four times under four cards it was 24
  // checkmarks for six facts, and it drowned the one number that differs.
  // Said once, above the ladder, it is also the strongest line on the page.
  const included = [
    t('pricing.feature1'),
    t('pricing.feature2'),
    t('pricing.feature3'),
    t('pricing.feature4'),
    t('pricing.feature5'),
    t('pricing.feature6'),
  ]

  // Plan names are product names — identical in every language.
  //
  // "Popular" is advice for someone still choosing, and it stops being that
  // the moment a subscription exists: on a Basic account it pointed a blue
  // frame at Pro's Subscribe button, which is disabled until they cancel —
  // a highlighted dead end — while also telling a paying customer that most
  // people picked something else. It already vanished for a Pro subscriber,
  // but only as a side effect of `own` overriding the highlight on the same
  // card; one case out of three is not a rule. Now it is one.
  const plans: SubscriptionPlan[] = [
    { name: 'Basic', price: '$2', priceNote: t('pricing.perMonth'), credits: 20 },
    { name: 'Pro', price: '$4', priceNote: t('pricing.perMonth'), credits: 50, highlighted: !isSubscribed },
    { name: 'Ultimate', price: '$9.99', priceNote: t('pricing.perMonth'), credits: 150 },
  ]

  function getAction(planName: string): 'current' | 'subscribe' | 'switch' {
    if (!isSubscribed) return 'subscribe'
    if (currentPlan && planName === currentPlan) return 'current'
    // Subscribed to a different plan: a second checkout would stack a second
    // billing subscription, so switching goes through cancellation first
    // (the backend refuses such checkouts too)
    return 'switch'
  }

  // The caption sits directly under the numeral and is read as its
  // continuation, so it has to agree with it — and Slavic languages inflect
  // the counted noun differently for 2–4 than for anything larger. One
  // string cannot serve 3 and 20 at once: Russian's "расшифровок в месяц" is
  // right under 20, 50 and 150 and is the wrong form of the word under 3.
  // English has one form and therefore two keys with the same text in it;
  // the translator's rule 4 says which number each one stands beneath.
  const unitFor = (count: number) =>
    count >= 2 && count <= 4 ? t('pricing.unitFew') : t('pricing.unit')

  return (
    <section id="pricing" className="py-16 bg-surface scroll-mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-text">{t('pricing.title')}</h2>
          <p className="mt-3 text-text-secondary max-w-lg mx-auto">{t('pricing.subtitle')}</p>
        </div>

        <div className="mb-6 rounded-2xl border border-border bg-surface-alt px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('pricing.included')}
          </p>
          <ul className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {included.map((feature) => (
              <li key={feature} className="flex items-start gap-1.5 text-sm leading-snug text-text-secondary">
                <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* One ladder, four rungs: the free tier is its first step, not a
            separate offer parked above the paid ones */}
        {/* auto-rows-fr: grid rows size themselves independently, so on a
            phone the bottom pair came out 18 px shorter than the top one */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-fr">
          <FreePlanCard
            credits={credits}
            isSubscribed={!!isSubscribed}
            loading={creditsLoading}
            renewsOn={renewsOn}
            unit={unitFor(FREE_MONTHLY_CREDITS)}
          />
          {plans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              action={getAction(plan.name)}
              unit={unitFor(plan.credits)}
              renewsOn={renewsOn}
              onSubscribe={onSubscribe}
              onCancelSubscription={onCancelSubscription}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
